import { useEffect, useImperativeHandle, useRef, useState, forwardRef, createContext, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosConfig from 'src/Resources/axiosConfig';
import DailySection, { DailySectionHandle } from './DailySection';
import useIsMobile from '../../../hooks/useIsMobile';

interface WeeklyReportProps {
  installationId: number;
  installationName?: string;
  installationEmail?: string;
  showEmail?: boolean;   // hide ALL email UI (auto-send checkboxes + manual Send Report) — products without email
  showRefresh?: boolean; // hide the Refresh Report button (keeps Download PDF)
  showHourly?: boolean;  // hide the Daily intraday hourly chart — SodistoreMax has no hourly data by design
}

interface DailyEnergyData {
  date: string;
  pvProduction: number;
  loadConsumption: number;
  gridImport: number;
  gridExport: number;
  batteryCharged: number;
  batteryDischarged: number;
}

interface WeeklySummary {
  totalPvProduction: number;
  totalConsumption: number;
  totalGridImport: number;
  totalGridExport: number;
  totalBatteryCharged: number;
  totalBatteryDischarged: number;
}

interface WeeklyReportResponse {
  installationName: string;
  periodStart: string;
  periodEnd: string;
  currentWeek: WeeklySummary;
  previousWeek: WeeklySummary | null;
  totalEnergySaved: number;
  totalSavingsCHF: number;
  daysEquivalent: number;
  selfSufficiencyPercent: number;
  selfConsumptionPercent: number;
  batteryEfficiencyPercent: number;
  gridDependencyPercent: number;
  pvChangePercent: number;
  consumptionChangePercent: number;
  gridImportChangePercent: number;
  dailyData: DailyEnergyData[];
  aiInsight: string;
  daysAvailable: number;
  daysExpected: number;
  missingDates: string[];
}

interface ReportSummary {
  installationId: number;
  periodStart: string;
  periodEnd: string;
  totalPvProduction: number;
  totalConsumption: number;
  totalGridImport: number;
  totalGridExport: number;
  totalBatteryCharged: number;
  totalBatteryDischarged: number;
  totalEnergySaved: number;
  totalSavingsCHF: number;
  selfSufficiencyPercent: number;
  selfConsumptionPercent: number;
  batteryEfficiencyPercent: number;
  gridDependencyPercent: number;
  aiInsight: string;
}

interface MonthlyReport extends ReportSummary {
  year: number;
  month: number;
  weekCount: number;
  avgPeakLoadHour: number;
  avgPeakSolarHour: number;
  avgWeekdayDailyLoad: number;
  avgWeekendDailyLoad: number;
  isPreview?: boolean;
  daysAvailable?: number;
  daysInMonth?: number;
}

interface YearlyReport extends ReportSummary {
  year: number;
  monthCount: number;
  avgPeakLoadHour: number;
  avgPeakSolarHour: number;
  avgWeekdayDailyLoad: number;
  avgWeekendDailyLoad: number;
  isPreview?: boolean;
}

interface WeeklyReportSummaryRecord {
  id: number;
  installationId: number;
  periodStart: string;
  periodEnd: string;
  totalPvProduction: number;
  totalConsumption: number;
  totalGridImport: number;
  totalGridExport: number;
  totalBatteryCharged: number;
  totalBatteryDischarged: number;
  totalEnergySaved: number;
  totalSavingsCHF: number;
  selfSufficiencyPercent: number;
  selfConsumptionPercent: number;
  batteryEfficiencyPercent: number;
  gridDependencyPercent: number;
  peakLoadHour: number;
  peakSolarHour: number;
  weekdayAvgDailyLoad: number;
  weekendAvgDailyLoad: number;
  aiInsight: string;
  createdAt: string;
}

function ReportHtmlFrame({ html }: { html: string }) {
  const isMobile = useIsMobile();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(600);
  // Narrowest width this report can lay out at without overflowing (its min-content
  // width), measured from the live document. 0 = "reflows to anything". The report
  // templates are fluid (width:100%;max-width:900px), so most reflow down to a phone
  // column and render at native size. The weekly one cannot: its Daily Breakdown has a
  // fixed 400px bar track + 80px day column, giving it a ~552px floor. Only reports
  // that hit their floor get scaled down, and only by as much as they must — laying
  // every report out at a hard-coded width instead made all of them needlessly tiny.
  const [contentFloor, setContentFloor] = useState(0);
  // Real width available to the report, measured from the wrapper. NOT window width:
  // the frame sits inside the sidebar + page/card/tab padding, so the window is much
  // wider than the column. Scaling against the window left the frame under-scaled and
  // the report clipped on the right.
  const [availW, setAvailW] = useState(0);
  const [winW, setWinW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // Kept in a ref so measure() can compare against the live column width without
  // re-subscribing the observers on every resize.
  const containerWRef = useRef(0);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // A previous report's floor must not carry over to the next one.
    setContentFloor(0);

    let bodyObserver: ResizeObserver | null = null;

    // Height comes from doc.body ONLY — deliberately never doc.documentElement.
    //
    // The report templates are standards mode (<!DOCTYPE html>), which makes documentElement the
    // SCROLLING ELEMENT, and its scrollHeight is the scrolling area — never smaller than the
    // viewport, which inside an iframe is the iframe's own height. So max(body, documentElement)
    // reads our own height back, and the +40 guarantees we exceed the content and land in exactly
    // that state. Then: setHeight -> iframe grows -> the auto-height wrapper grows -> wrapObserver
    // fires -> measure() -> +40 again, unbounded. That is the "huge gap" under weekly/monthly/yearly
    // reports (all three share this component), and it is the same ratchet the width axis below
    // already guards against.
    //
    // body is safe to measure: the backend templates set body{margin:0;padding:0} with no
    // height:100%, so body is not the scrolling element and its scrollHeight is pure content.
    // The functional update makes this converge — content height is constant, so it settles after
    // one pass instead of chasing itself.
    const applyHeight = (doc: Document) => {
      const newHeight = doc.body.scrollHeight + 40;
      if (newHeight > 50) setHeight((h) => (Math.abs(newHeight - h) > 1 ? newHeight : h));
    };

    const measure = () => {
      const wrapW = wrapRef.current?.clientWidth ?? 0;
      if (wrapW > 0) setAvailW(wrapW);
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc?.body) return;

        applyHeight(doc);

        // If the content is wider than the width the iframe is actually laid out at,
        // it could not reflow any narrower — that overflow IS the min-content floor.
        // (offsetWidth is the layout width, unaffected by the scale transform.)
        //
        // Only trust this while the iframe is still laid out at the column width. Once
        // we have widened it to the floor, a report that overflows by a fixed amount
        // relative to its container would report a bigger floor every pass and ratchet
        // itself upward forever. Measuring only from an un-inflated layout closes that
        // loop; the clamp is a last-resort backstop.
        const cw = Math.max(doc.body.scrollWidth, doc.documentElement.scrollWidth);
        const laidOutAt = iframe.offsetWidth;
        const container = containerWRef.current;
        if (container > 0 && laidOutAt <= container + 1 && cw > laidOutAt + 1) {
          setContentFloor((f) => Math.max(f, Math.min(cw, container * 4)));
        }

        if (!bodyObserver) {
          bodyObserver = new ResizeObserver(() => applyHeight(doc));
          bodyObserver.observe(doc.body);
        }
      } catch { /* cross-origin safety */ }
    };

    // A report mounted in a hidden tab (display:none) measures 0 for everything, and
    // there is no load/resize event when that tab is finally shown. Observing the
    // wrapper covers it: going from no box to a real box is a size change, so this
    // fires exactly once when the report first becomes visible.
    const wrapObserver = new ResizeObserver(measure);
    if (wrapRef.current) wrapObserver.observe(wrapRef.current);

    const onResize = () => { setWinW(window.innerWidth); measure(); };
    iframe.addEventListener('load', measure);
    const timers = [200, 500, 1000, 1800].map(ms => setTimeout(measure, ms));
    window.addEventListener('resize', onResize);

    return () => {
      iframe.removeEventListener('load', measure);
      window.removeEventListener('resize', onResize);
      timers.forEach(clearTimeout);
      wrapObserver.disconnect();
      bodyObserver?.disconnect();
    };
  }, [html]);

  // Desktop: width 100%, no scale → byte-identical to the original.
  // Mobile: lay the report out at the column width so it reflows and stays at native
  // size; only if it cannot reflow that narrow (contentFloor) do we lay it out at the
  // floor and scale that down to fit exactly. Window width is a pre-measure fallback
  // only, so the first paint is not wildly off.
  const containerW = availW > 0 ? availW : Math.min(winW - 32, 900);
  containerWRef.current = containerW;
  const layoutW = Math.max(contentFloor, containerW);
  const scale = isMobile && layoutW > containerW ? containerW / layoutW : 1;
  const scaled = scale < 1;

  return (
    // Height is pinned only when scaling, so the wrapper crops the scaled-down
    // iframe. Left auto on desktop: a fixed height would clip the line-box
    // descender under the inline iframe and shift everything below up a few px.
    <div
      ref={wrapRef}
      style={{
        width: '100%',
        height: scaled ? height * scale : undefined,
        overflow: 'hidden'
      }}
    >
      <iframe
        ref={iframeRef}
        srcDoc={html}
        scrolling="no"
        style={{
          width: isMobile ? layoutW : '100%',
          height,
          border: 'none',
          overflow: 'hidden',
          transform: scaled ? `scale(${scale})` : undefined,
          transformOrigin: 'top left',
        }}
        sandbox="allow-same-origin"
        title="report"
      />
    </div>
  );
}

// Matches: time ranges (14:00–18:00), times (09:00), decimals (126.4 / 1,3), integers (34)
const BOLD_PATTERN = /(\d{1,2}:\d{2}(?:[–\-]\d{1,2}:\d{2})?|\d+[.,]\d+|\d+)/g;
const isBold = (s: string) => /\d/.test(s);

function FormattedBullet({ text }: { text: string }) {
  const colonIdx = text.indexOf(':');
  if (colonIdx > 0) {
    const title = text.slice(0, colonIdx);
    const rest  = text.slice(colonIdx + 1);
    const restParts = rest.split(BOLD_PATTERN).map((p, i) =>
      isBold(p) ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>
    );
    return <><strong>{title}</strong>:{restParts}</>;
  }
  const parts = text.split(BOLD_PATTERN).map((p, i) =>
    isBold(p) ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>
  );
  return <>{parts}</>;
}

function getMonthName(month: number, locale: string): string {
  const date = new Date(2000, month - 1, 1);
  return date.toLocaleDateString(locale, { month: 'long' });
}

// ── Email Bar (shared) ──────────────────────────────────────────

function EmailBar({ onSend, disabled }: { onSend: (email: string) => Promise<void>; disabled?: boolean }) {
  const intl = useIntl();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const handleSend = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await onSend(email.trim());
      setSendStatus({ message: intl.formatMessage({ id: 'reportSentTo' }, { email }), severity: 'success' });
    } catch {
      setSendStatus({ message: intl.formatMessage({ id: 'reportSendError' }), severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, width: '100%', mb: 3, gap: 0.5 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, width: { xs: '100%', sm: 'auto' }, gap: 1 }}>
        <TextField
          size="small"
          placeholder="recipient@example.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSendStatus(null); }}
          sx={{ width: { xs: '100%', sm: 280 } }}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
        />
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !email.trim() || disabled}
          sx={{ bgcolor: '#2c3e50', '&:hover': { bgcolor: '#34495e' } }}
        >
          <FormattedMessage id="sendReport" defaultMessage="Send Report" />
        </Button>
      </Box>
      {sendStatus && (
        <Typography variant="caption" sx={{ color: sendStatus.severity === 'success' ? '#27ae60' : '#e74c3c' }}>
          {sendStatus.message}
        </Typography>
      )}
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────

// Whether email UI (auto-send + manual Send Report) is shown; provided by WeeklyReport
// (false for products without email, e.g. SodistoreMax) and consumed by the report sub-sections.
const ShowEmailContext = createContext(true);

function WeeklyReport({ installationId, installationName, installationEmail, showEmail = true, showRefresh = true, showHourly = true }: WeeklyReportProps) {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useIsMobile();
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([]);
  const [yearlyReports, setYearlyReports] = useState<YearlyReport[]>([]);
  const [monthlyPreview, setMonthlyPreview] = useState<MonthlyReport | null>(null);
  const [yearlyPreview, setYearlyPreview] = useState<YearlyReport | null>(null);
  const [selectedMonthlyIdx, setSelectedMonthlyIdx] = useState(0);
  const [selectedYearlyIdx, setSelectedYearlyIdx] = useState(0);
  const [dailyHasData, setDailyHasData] = useState(false);
  const [weeklyHasData, setWeeklyHasData] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reportPeriods, setReportPeriods] = useState<Record<string, { start: string; end: string; year?: number; month?: number }>>({});
  const weeklyRef = useRef<WeeklySectionHandle>(null);
  const dailyRef = useRef<DailySectionHandle>(null);

  // Auto-send email preferences
  const [autoSend, setAutoSend] = useState({ sendWeekly: false, sendMonthly: false, sendYearly: false });
  const [autoSendDirty, setAutoSendDirty] = useState(false);
  const [savingAutoSend, setSavingAutoSend] = useState(false);
  const [autoSendStatus, setAutoSendStatus] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    axiosConfig.get('/GetEmailPreference', { params: { installationId } })
      .then(res => {
        setAutoSend({ sendWeekly: res.data.sendWeekly, sendMonthly: res.data.sendMonthly, sendYearly: res.data.sendYearly });
        setAutoSendDirty(false);
      })
      .catch(() => {});
  }, [installationId]);

  const handleAutoSendChange = (field: 'sendWeekly' | 'sendMonthly' | 'sendYearly') => {
    setAutoSend(prev => ({ ...prev, [field]: !prev[field] }));
    setAutoSendDirty(true);
  };

  const handleSaveAutoSend = async () => {
    setSavingAutoSend(true);
    try {
      await axiosConfig.post('/UpdateEmailPreference', null, {
        params: { installationId, ...autoSend }
      });
      setAutoSendDirty(false);
      setAutoSendStatus({ message: intl.formatMessage({ id: 'autoSendSaved', defaultMessage: 'Auto-send preferences saved.' }), severity: 'success' });
      setTimeout(() => setAutoSendStatus(null), 4000);
    } catch {
      setAutoSendStatus({ message: intl.formatMessage({ id: 'autoSendSaveFailed', defaultMessage: 'Failed to save auto-send preferences.' }), severity: 'error' });
      setTimeout(() => setAutoSendStatus(null), 4000);
    } finally {
      setSavingAutoSend(false);
    }
  };

  const hasEmail = !!(installationEmail && installationEmail.trim());

  const fetchReportData = () => {
    const lang = intl.locale;
    axiosConfig.get('/GetMonthlyReports', { params: { installationId, language: lang } })
      .then(res => setMonthlyReports(res.data))
      .catch(() => {});
    axiosConfig.get('/GetYearlyReports', { params: { installationId, language: lang } })
      .then(res => setYearlyReports(res.data))
      .catch(() => {});
    axiosConfig.get('/GetCurrentMonthPreview', { params: { installationId, language: lang } })
      .then(res => setMonthlyPreview(res.data))
      .catch(() => setMonthlyPreview(null));
    axiosConfig.get('/GetCurrentYearPreview', { params: { installationId, language: lang } })
      .then(res => setYearlyPreview(res.data))
      .catch(() => setYearlyPreview(null));
  };

  useEffect(() => { fetchReportData(); }, [installationId, intl.locale]);

  // Phones use short period labels (Day/Week/Month/Year) — the full German words
  // (Wöchentlich/Jährlich) overflow the narrow tab bar and clip the last tab.
  // Desktop keeps the full words.
  const tabs = [
    { long: 'dailyTab', short: 'dailyTabShort', ld: 'Daily', sd: 'Day', key: 'daily' },
    { long: 'weeklyTab', short: 'weeklyTabShort', ld: 'Weekly', sd: 'Week', key: 'weekly' },
    { long: 'monthlyTab', short: 'monthlyTabShort', ld: 'Monthly', sd: 'Month', key: 'monthly' },
    { long: 'yearlyTab', short: 'yearlyTabShort', ld: 'Yearly', sd: 'Year', key: 'yearly' }
  ].map((t) => ({
    label: intl.formatMessage({
      id: isMobile ? t.short : t.long,
      defaultMessage: isMobile ? t.sd : t.ld
    }),
    key: t.key
  }));

  const safeTab = Math.min(activeTab, tabs.length - 1);

  const activeTabHasData = (() => {
    const key = tabs[safeTab]?.key;
    if (key === 'daily') return dailyHasData;
    if (key === 'weekly') return weeklyHasData;
    if (key === 'monthly') return monthlyReports.length > 0 || monthlyPreview !== null;
    if (key === 'yearly') return yearlyReports.length > 0 || yearlyPreview !== null;
    return false;
  })();

  const handleDownloadPdf = async () => {
    const reportType = tabs[safeTab]?.key ?? 'report';
    let endpoint = '';
    const params: Record<string, any> = { installationId, language: intl.locale };

    switch (reportType) {
      case 'daily':
        endpoint = '/GetDailyReportHtml';
        if (reportPeriods.daily?.start) params.date = reportPeriods.daily.start;
        break;
      case 'weekly':
        endpoint = '/GetWeeklyReportHtml';
        break;
      case 'monthly':
        endpoint = '/GetMonthlyReportHtml';
        if (reportPeriods.monthly?.year) params.year = reportPeriods.monthly.year;
        if (reportPeriods.monthly?.month) params.month = reportPeriods.monthly.month;
        break;
      case 'yearly':
        endpoint = '/GetYearlyReportHtml';
        if (reportPeriods.yearly?.year) params.year = reportPeriods.yearly.year;
        break;
    }

    if (!endpoint) return;

    setDownloadingPdf(true);
    try {
      const res = await axiosConfig.get(endpoint, { params, responseType: 'text' });
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const activePeriod = reportPeriods[reportType] ?? null;
      const dateRange = activePeriod
        ? `${activePeriod.start.replace(/-/g, '')}-${activePeriod.end.replace(/-/g, '')}`
        : new Date().toISOString().split('T')[0].replace(/-/g, '');

      const safeName = (installationName || String(installationId))
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9\-_]/g, '');

      const pdfTitle = `${safeName}-${reportType}-${dateRange}`;
      let html = res.data as string;
      if (html.includes('<head>')) {
        html = html.replace('<head>', `<head><title>${pdfTitle}</title>`);
      } else if (html.includes('<html>')) {
        html = html.replace('<html>', `<html><head><title>${pdfTitle}</title></head>`);
      }

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.document.title = pdfTitle;
      printWindow.onafterprint = () => printWindow.close();
      const imgs = printWindow.document.querySelectorAll('img');
      const allLoaded = Array.from(imgs).map(img =>
        img.complete ? Promise.resolve() : new Promise<void>(r => { img.onload = () => r(); img.onerror = () => r(); })
      );
      Promise.all(allLoaded).then(() => setTimeout(() => printWindow.print(), 200));
    } catch (err) {
      console.error('PDF download failed', err);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleRefreshReport = async () => {
    const reportType = tabs[safeTab]?.key ?? '';
    setRefreshing(true);
    try {
      if (reportType === 'daily') {
        await dailyRef.current?.refresh();
      } else if (reportType === 'weekly') {
        await weeklyRef.current?.regenerate();
      } else if (reportType === 'monthly') {
        const period = reportPeriods.monthly;
        if (period?.year && period?.month) {
          await axiosConfig.post('/TriggerMonthlyAggregation', null, {
            params: { installationId, year: period.year, month: period.month }
          });
          fetchReportData();
        }
      } else if (reportType === 'yearly') {
        const period = reportPeriods.yearly;
        if (period?.year) {
          await axiosConfig.post('/TriggerYearlyAggregation', null, {
            params: { installationId, year: period.year }
          });
          fetchReportData();
        }
      }
    } catch (err) {
      console.error('Report refresh failed', err);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <ShowEmailContext.Provider value={showEmail}>
    <Box sx={{ p: 2, width: '100%', maxWidth: 900, mx: 'auto' }} className="report-container">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          // Phone: let the Refresh/Download buttons wrap below the tabs instead of
          // sharing their row and pushing the last tab (Yearly) off the card edge.
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          rowGap: 1
        }}
        className="no-print"
      >
        <Tabs
          value={safeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flex: 1,
            // Take the whole first row on phones so the buttons wrap under it, and
            // shrink each tab to its label so all four (Daily/Weekly/Monthly/Yearly)
            // fit without needing to scroll.
            minWidth: isMobile ? '100%' : undefined,
            '& .MuiTab-root': {
              fontWeight: 'bold',
              ...(isMobile && { minWidth: 'auto', minHeight: 42, px: 1.2, fontSize: '0.82rem' })
            }
          }}
        >
          {tabs.map(t => <Tab key={t.key} label={t.label} />)}
        </Tabs>
        {showRefresh && (
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefreshReport}
            disabled={refreshing || downloadingPdf}
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          >
            <FormattedMessage id={refreshing ? 'refreshing' : 'refreshReport'} defaultMessage={refreshing ? 'Refreshing...' : 'Refresh Report'} />
          </Button>
        )}
        {activeTabHasData && (
          <Button
            variant="outlined"
            startIcon={downloadingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || refreshing}
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          >
            <FormattedMessage id="downloadPdf" defaultMessage="Download PDF" />
          </Button>
        )}
      </Box>

      {showEmail && (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }} className="no-print">
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#555' }}>
          <FormattedMessage id="autoSendReports" defaultMessage="Auto-send reports:" />
        </Typography>
        <Tooltip title={!hasEmail ? intl.formatMessage({ id: 'autoSendNoEmail', defaultMessage: 'Set email address in Information tab to enable auto-send' }) : ''}>
          <span>
            <FormControlLabel
              control={<Checkbox checked={autoSend.sendWeekly} onChange={() => handleAutoSendChange('sendWeekly')} disabled={!hasEmail} size="small" />}
              label={intl.formatMessage({ id: 'weeklyTab', defaultMessage: 'Weekly' })}
            />
          </span>
        </Tooltip>
        <Tooltip title={!hasEmail ? intl.formatMessage({ id: 'autoSendNoEmail', defaultMessage: 'Set email address in Information tab to enable auto-send' }) : ''}>
          <span>
            <FormControlLabel
              control={<Checkbox checked={autoSend.sendMonthly} onChange={() => handleAutoSendChange('sendMonthly')} disabled={!hasEmail} size="small" />}
              label={intl.formatMessage({ id: 'monthlyTab', defaultMessage: 'Monthly' })}
            />
          </span>
        </Tooltip>
        <Tooltip title={!hasEmail ? intl.formatMessage({ id: 'autoSendNoEmail', defaultMessage: 'Set email address in Information tab to enable auto-send' }) : ''}>
          <span>
            <FormControlLabel
              control={<Checkbox checked={autoSend.sendYearly} onChange={() => handleAutoSendChange('sendYearly')} disabled={!hasEmail} size="small" />}
              label={intl.formatMessage({ id: 'yearlyTab', defaultMessage: 'Yearly' })}
            />
          </span>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={savingAutoSend ? <CircularProgress size={14} /> : <SaveIcon />}
          onClick={handleSaveAutoSend}
          disabled={!autoSendDirty || savingAutoSend}
          sx={{ ml: 1 }}
        >
          <FormattedMessage id="applyChanges" defaultMessage="Save" />
        </Button>
        {!hasEmail && (
          <Typography variant="caption" sx={{ color: '#e67e22', ml: 1 }}>
            <FormattedMessage id="autoSendNoEmail" defaultMessage="Set email address in Information tab to enable auto-send" />
          </Typography>
        )}
        {autoSendStatus && (
          <Typography variant="caption" sx={{ color: autoSendStatus.severity === 'success' ? '#27ae60' : '#e74c3c', ml: 1, textTransform: 'uppercase', fontWeight: 600 }}>
            {autoSendStatus.message}
          </Typography>
        )}
      </Box>
      )}

      <Box sx={{ display: tabs[safeTab]?.key === 'daily' ? 'block' : 'none', minHeight: '50vh' }}>
        <DailySection ref={dailyRef} installationId={installationId} showHourly={showHourly} onHasData={setDailyHasData} onPeriodChange={(date: string) => setReportPeriods(prev => ({ ...prev, daily: { start: date, end: date } }))} />
      </Box>
      <Box sx={{ display: tabs[safeTab]?.key === 'weekly' ? 'block' : 'none', minHeight: '50vh' }}>
        <WeeklySection
          ref={weeklyRef}
          installationId={installationId}
          latestMonthlyPeriodEnd={
            monthlyReports.length > 0
              ? monthlyReports.reduce((a, b) => a.periodEnd > b.periodEnd ? a : b).periodEnd
              : null
          }
          onHasData={setWeeklyHasData}
          onPeriodChange={(start, end) => setReportPeriods(prev => ({ ...prev, weekly: { start, end } }))}
        />
      </Box>
      <Box sx={{ display: tabs[safeTab]?.key === 'monthly' ? 'block' : 'none', minHeight: '50vh' }}>
        <MonthlySection
          installationId={installationId}
          reports={monthlyReports}
          preview={monthlyPreview}
          selectedIdx={selectedMonthlyIdx}
          onSelectedIdxChange={setSelectedMonthlyIdx}
          onPeriodChange={(r: MonthlyReport) => setReportPeriods(prev => ({ ...prev, monthly: { start: r.periodStart, end: r.periodEnd, year: r.year, month: r.month } }))}
        />
      </Box>
      <Box sx={{ display: tabs[safeTab]?.key === 'yearly' ? 'block' : 'none', minHeight: '50vh' }}>
        <YearlySection
          installationId={installationId}
          reports={yearlyReports}
          preview={yearlyPreview}
          selectedIdx={selectedYearlyIdx}
          onSelectedIdxChange={setSelectedYearlyIdx}
          onPeriodChange={(r: YearlyReport) => setReportPeriods(prev => ({ ...prev, yearly: { start: r.periodStart, end: r.periodEnd, year: r.year } }))}
        />
      </Box>
    </Box>
    </ShowEmailContext.Provider>
  );
}

// ── Weekly Section (existing weekly report content) ────────────

interface WeeklySectionHandle {
  regenerate: () => Promise<void>;
}

const WeeklySection = forwardRef<WeeklySectionHandle, { installationId: number; latestMonthlyPeriodEnd: string | null; onHasData?: (hasData: boolean) => void; onPeriodChange?: (start: string, end: string) => void }>(
  ({ installationId, latestMonthlyPeriodEnd, onHasData, onPeriodChange }, ref) => {
  const intl = useIntl();
  const showEmail = useContext(ShowEmailContext);
  const [report, setReport] = useState<WeeklyReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string | null>(null);

  useEffect(() => {
    fetchReport();
  }, [installationId, intl.locale]);

  const fetchReport = async (forceRegenerate = false) => {
    setLoading(true);
    setError(null);
    try {
      // When regenerating, fetch JSON first (populates cache), then HTML (reads fresh cache)
      // When not regenerating, fetch both in parallel (both read from cache)
      if (forceRegenerate) {
        const jsonRes = await axiosConfig.get('/GetWeeklyReport', {
          params: { installationId, language: intl.locale, forceRegenerate }
        });
        const htmlRes = await axiosConfig.get('/GetWeeklyReportHtml', {
          params: { installationId, language: intl.locale, source: 'web' },
          responseType: 'text'
        });
        setReport(jsonRes.data);
        setReportHtml(htmlRes.data);
        onHasData?.(true);
        onPeriodChange?.(jsonRes.data.periodStart, jsonRes.data.periodEnd);
      } else {
        const [jsonRes, htmlRes] = await Promise.all([
          axiosConfig.get('/GetWeeklyReport', {
            params: { installationId, language: intl.locale }
          }),
          axiosConfig.get('/GetWeeklyReportHtml', {
            params: { installationId, language: intl.locale, source: 'web' },
            responseType: 'text'
          })
        ]);
        setReport(jsonRes.data);
        setReportHtml(htmlRes.data);
        onHasData?.(true);
        onPeriodChange?.(jsonRes.data.periodStart, jsonRes.data.periodEnd);
      }
    } catch (err: any) {
      const msg =
        err.response?.data ||
        err.message ||
        intl.formatMessage({ id: 'failedToLoadReport' });
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      onHasData?.(false);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ regenerate: () => fetchReport(true) }));

  const handleSendEmail = async (emailAddress: string) => {
    await axiosConfig.post('/SendWeeklyReportEmail', null, {
      params: { installationId, emailAddress }
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6
        }}
      >
        <CircularProgress size={50} style={{ color: '#ffc04d' }} />
        <Typography variant="body2" mt={2}>
          <FormattedMessage id="generatingReport" defaultMessage="Generating weekly report..." />
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Alert severity="warning">
          <FormattedMessage id="noReportData" defaultMessage="No report data found." />
        </Alert>
      </Box>
    );
  }

  if (!report) return null;

  return (
    <>
      {/* Email bar */}
      {showEmail && <EmailBar onSend={handleSendEmail} />}

      {/* Report content — rendered from backend HTML (single source of truth) */}
      {reportHtml && <ReportHtmlFrame html={reportHtml} />}

      {/* Weekly History — weeks not yet captured in a monthly report */}
      <WeeklyHistory
        installationId={installationId}
        latestMonthlyPeriodEnd={latestMonthlyPeriodEnd}
        currentReportPeriodEnd={report?.periodEnd ?? null}
      />
    </>
  );
});

// ── Weekly History (saved weekly reports for current month) ─────

function WeeklyHistory({ installationId, latestMonthlyPeriodEnd, currentReportPeriodEnd }: {
  installationId: number;
  latestMonthlyPeriodEnd: string | null;
  currentReportPeriodEnd: string | null;
}) {
  const intl = useIntl();
  const [records, setRecords] = useState<WeeklyReportSummaryRecord[]>([]);

  useEffect(() => {
    const now   = new Date();
    const year  = now.getFullYear();
    const month = now.getMonth() + 1;
    const lang  = intl.locale;

    // Fetch current month and previous month to cover cross-month weeks
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear  = month === 1 ? year - 1 : year;

    Promise.all([
      axiosConfig.get('/GetWeeklyReportSummaries', { params: { installationId, year, month, language: lang } })
        .then(r => r.data as WeeklyReportSummaryRecord[]).catch(() => [] as WeeklyReportSummaryRecord[]),
      axiosConfig.get('/GetWeeklyReportSummaries', { params: { installationId, year: prevYear, month: prevMonth, language: lang } })
        .then(r => r.data as WeeklyReportSummaryRecord[]).catch(() => [] as WeeklyReportSummaryRecord[])
    ]).then(([cur, prev]) => {
      // Combine, deduplicate by id, sort descending by periodEnd
      const all    = [...cur, ...prev];
      const unique = Array.from(new Map(all.map(r => [r.id, r])).values())
        .sort((a, b) => b.periodEnd.localeCompare(a.periodEnd));

      // Exclude the current week already shown as the main report above
      const withoutCurrent = currentReportPeriodEnd
        ? unique.filter(r => r.periodEnd !== currentReportPeriodEnd)
        : unique;

      // Keep only weeks not yet covered by a monthly report
      const uncovered = latestMonthlyPeriodEnd
        ? withoutCurrent.filter(r => r.periodEnd > latestMonthlyPeriodEnd)
        : withoutCurrent;

      // Always show at least the latest non-current week even if all are covered
      const toShow = uncovered.length > 0 ? uncovered : withoutCurrent.slice(0, 1);
      setRecords(toShow);
    });
  }, [installationId, latestMonthlyPeriodEnd, currentReportPeriodEnd, intl.locale]);

  if (records.length === 0) return null;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
        <FormattedMessage id="recentWeeklyReports" defaultMessage="Recent Weekly Reports" />
      </Typography>
      {records.map((rec) => (
        <Accordion key={rec.id} sx={{ mb: 1, '&:before': { display: 'none' } }} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {rec.periodStart} — {rec.periodEnd}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                PV {rec.totalPvProduction.toFixed(1)} kWh | {intl.formatMessage({ id: 'consumption', defaultMessage: 'Consumption' })} {rec.totalConsumption.toFixed(1)} kWh
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#27ae60">{rec.totalEnergySaved.toFixed(1)} kWh</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="solarEnergyUsed" defaultMessage="Energy Saved" /></Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#2980b9">~{rec.totalSavingsCHF.toFixed(0)} CHF</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="estMoneySaved" defaultMessage="Est. Money Saved" /></Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#8e44ad">{rec.selfSufficiencyPercent.toFixed(0)}%</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="solarCoverage" defaultMessage="Energy Independence" /></Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" fontWeight="bold" color="#e67e22">{rec.batteryEfficiencyPercent.toFixed(0)}%</Typography>
                  <Typography variant="caption" color="#888"><FormattedMessage id="batteryEfficiency" defaultMessage="Battery Eff." /></Typography>
                </Box>
              </Grid>
            </Grid>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', '& td': { p: 0.8, borderBottom: '1px solid #f0f0f0' } }}>
              <tbody>
                <tr>
                  <td><FormattedMessage id="gridImport" defaultMessage="Grid Import" /></td>
                  <td style={{ textAlign: 'right' }}>{rec.totalGridImport.toFixed(1)} kWh</td>
                  <td style={{ width: 20 }} />
                  <td><FormattedMessage id="gridExport" defaultMessage="Grid Export" /></td>
                  <td style={{ textAlign: 'right' }}>{rec.totalGridExport.toFixed(1)} kWh</td>
                </tr>
                <tr>
                  <td><FormattedMessage id="batteryInOut" defaultMessage="Battery In / Out" /></td>
                  <td style={{ textAlign: 'right' }} colSpan={4}>{rec.totalBatteryCharged.toFixed(1)} / {rec.totalBatteryDischarged.toFixed(1)} kWh</td>
                </tr>
              </tbody>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
}

// ── Monthly Section ─────────────────────────────────────────────

function MonthlySection({
  installationId,
  reports,
  preview,
  selectedIdx,
  onSelectedIdxChange,
  onPeriodChange
}: {
  installationId: number;
  reports: MonthlyReport[];
  preview: MonthlyReport | null;
  selectedIdx: number;
  onSelectedIdxChange: (idx: number) => void;
  onPeriodChange?: (report: MonthlyReport) => void;
}) {
  const intl = useIntl();

  // Check if current month already has a finalized report
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const hasFinalizedCurrentMonth = reports.some(r => r.year === currentYear && r.month === currentMonth);

  // Show preview only if no finalized report for current month exists
  const showPreview = preview && !hasFinalizedCurrentMonth;

  return (
    <>
      {/* Current month preview */}
      {showPreview && (
        <Paper sx={{ mb: 3, border: '1px solid #f0ad4e', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: '#2c3e50', color: '#fff', p: 3, borderRadius: '8px 8px 0 0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight="bold">
                <FormattedMessage id="monthlyReportTitle" defaultMessage="Monthly Performance Report" />
              </Typography>
              <Typography variant="caption" sx={{ bgcolor: '#f0ad4e', color: '#000', px: 1, py: 0.3, borderRadius: 1, fontWeight: 'bold' }}>
                <FormattedMessage id="reportInProgress" defaultMessage="{month} (in progress)" values={{ month: '' }} />
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              <FormattedMessage
                id="reportInProgress"
                defaultMessage="{month} (in progress)"
                values={{ month: `${getMonthName(preview.month, intl.locale)} ${preview.year}` }}
              />
            </Typography>
            {(preview.daysAvailable ?? 0) < (preview.daysInMonth ?? 0) && (
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                <FormattedMessage
                  id="daysOfTotal"
                  defaultMessage="{available} of {total} days"
                  values={{ available: preview.daysAvailable ?? 0, total: preview.daysInMonth ?? 0 }}
                />
              </Typography>
            )}
          </Box>

          {/* Preview content — reuse InsightBox + SavingsCards */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
              <FormattedMessage id="monthlyInsights" defaultMessage="Monthly Insights" />
            </Typography>
            <InsightBox
              text={preview.aiInsight || ''}
              bullets={(preview.aiInsight || '').split(/\n+/).map(l => l.replace(/\*\*/g, '').replace(/^[\d]+[.)]\s*/, '').replace(/^[-*]\s*/, '').trim()).filter(l => l.length > 0)}
            />
          </Box>

          <Box sx={{ px: 3, pb: 2 }}>
            <SavingsCards
              intl={intl}
              energySaved={preview.totalEnergySaved}
              savingsCHF={preview.totalSavingsCHF}
              selfSufficiency={preview.selfSufficiencyPercent}
              batteryEfficiency={preview.batteryEfficiencyPercent}
            />
          </Box>

          <Alert severity="info" sx={{ mx: 3, mb: 3 }}>
            <FormattedMessage id="monthlyAutoNote" defaultMessage="Final report will be automatically generated on the 1st of next month." />
          </Alert>
        </Paper>
      )}

      {/* Existing monthly reports */}
      {reports.length > 0 ? (
        <AggregatedSection
          reports={reports}
          type="monthly"
          labelFn={(r: MonthlyReport) => `${getMonthName(r.month, intl.locale)} ${r.year}`}
          countLabelId="weeksAggregated"
          countFn={(r: MonthlyReport) => r.weekCount}
          sendEndpoint="/SendMonthlyReportEmail"
          sendParamsFn={(r: MonthlyReport) => ({ installationId, year: r.year, month: r.month })}
          htmlEndpoint="/GetMonthlyReportHtml"
          htmlParamsFn={(r: MonthlyReport) => ({ year: r.year, month: r.month })}
          installationId={installationId}
          controlledIdx={selectedIdx}
          onIdxChange={onSelectedIdxChange}
          onPeriodChange={onPeriodChange}
        />
      ) : !showPreview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <Alert severity="warning">
            <FormattedMessage id="noReportData" defaultMessage="No report data found." />
          </Alert>
        </Box>
      ) : null}
    </>
  );
}

// ── Yearly Section ──────────────────────────────────────────────

function YearlySection({
  installationId,
  reports,
  preview,
  selectedIdx,
  onSelectedIdxChange,
  onPeriodChange
}: {
  installationId: number;
  reports: YearlyReport[];
  preview: YearlyReport | null;
  selectedIdx: number;
  onSelectedIdxChange: (idx: number) => void;
  onPeriodChange?: (report: YearlyReport) => void;
}) {
  const intl = useIntl();

  const currentYear = new Date().getFullYear();
  const hasFinalizedCurrentYear = reports.some(r => r.year === currentYear);
  const showPreview = preview && !hasFinalizedCurrentYear;

  return (
    <>
      {/* Current year preview */}
      {showPreview && (
        <Paper sx={{ mb: 3, border: '1px solid #f0ad4e', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: '#2c3e50', color: '#fff', p: 3, borderRadius: '8px 8px 0 0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight="bold">
                <FormattedMessage id="yearlyReportTitle" defaultMessage="Annual Performance Report" />
              </Typography>
              <Typography variant="caption" sx={{ bgcolor: '#f0ad4e', color: '#000', px: 1, py: 0.3, borderRadius: 1, fontWeight: 'bold' }}>
                <FormattedMessage id="reportInProgress" defaultMessage="{month} (in progress)" values={{ month: '' }} />
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              <FormattedMessage
                id="reportInProgress"
                defaultMessage="{month} (in progress)"
                values={{ month: `${preview.year}` }}
              />
            </Typography>
            {preview.monthCount < 12 && (
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                <FormattedMessage
                  id="monthsOfTotal"
                  defaultMessage="{available} of {total} months"
                  values={{ available: preview.monthCount, total: 12 }}
                />
              </Typography>
            )}
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
              <FormattedMessage id="yearlyInsights" defaultMessage="Annual Insights" />
            </Typography>
            <InsightBox
              text={preview.aiInsight || ''}
              bullets={(preview.aiInsight || '').split(/\n+/).map(l => l.replace(/\*\*/g, '').replace(/^[\d]+[.)]\s*/, '').replace(/^[-*]\s*/, '').trim()).filter(l => l.length > 0)}
            />
          </Box>

          <Box sx={{ px: 3, pb: 2 }}>
            <SavingsCards
              intl={intl}
              energySaved={preview.totalEnergySaved}
              savingsCHF={preview.totalSavingsCHF}
              selfSufficiency={preview.selfSufficiencyPercent}
              batteryEfficiency={preview.batteryEfficiencyPercent}
            />
          </Box>

          <Alert severity="info" sx={{ mx: 3, mb: 3 }}>
            <FormattedMessage id="yearlyAutoNote" defaultMessage="Final report will be automatically generated on January 2nd." />
          </Alert>
        </Paper>
      )}

      {/* Existing yearly reports */}
      {reports.length > 0 ? (
        <AggregatedSection
          reports={reports}
          type="yearly"
          labelFn={(r: YearlyReport) => `${r.year}`}
          countLabelId="monthsAggregated"
          countFn={(r: YearlyReport) => r.monthCount}
          sendEndpoint="/SendYearlyReportEmail"
          sendParamsFn={(r: YearlyReport) => ({ installationId, year: r.year })}
          htmlEndpoint="/GetYearlyReportHtml"
          htmlParamsFn={(r: YearlyReport) => ({ year: r.year })}
          installationId={installationId}
          controlledIdx={selectedIdx}
          onIdxChange={onSelectedIdxChange}
          onPeriodChange={onPeriodChange}
        />
      ) : !showPreview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <Alert severity="warning">
            <FormattedMessage id="noReportData" defaultMessage="No report data found." />
          </Alert>
        </Box>
      ) : null}
    </>
  );
}

// ── Aggregated Section (Monthly / Yearly) ──────────────────────

function AggregatedSection<T extends ReportSummary>({
  reports,
  type,
  labelFn,
  countLabelId,
  countFn,
  sendEndpoint,
  sendParamsFn,
  htmlEndpoint,
  htmlParamsFn,
  installationId,
  controlledIdx,
  onIdxChange,
  onPeriodChange
}: {
  reports: T[];
  type: 'monthly' | 'yearly';
  labelFn: (r: T) => string;
  countLabelId: string;
  countFn: (r: T) => number;
  sendEndpoint: string;
  sendParamsFn: (r: T) => object;
  htmlEndpoint: string;
  htmlParamsFn: (r: T) => Record<string, any>;
  installationId: number;
  controlledIdx?: number;
  onIdxChange?: (idx: number) => void;
  onPeriodChange?: (report: T) => void;
}) {
  const intl = useIntl();
  const showEmail = useContext(ShowEmailContext);
  const [internalIdx, setInternalIdx] = useState(0);
  const selectedIdx = controlledIdx ?? internalIdx;
  const handleIdxChange = (idx: number) => {
    setInternalIdx(idx);
    onIdxChange?.(idx);
    if (reports[idx]) onPeriodChange?.(reports[idx]);
  };

  // Report initial period on mount
  useEffect(() => {
    if (reports.length > 0 && reports[selectedIdx]) {
      onPeriodChange?.(reports[selectedIdx]);
    }
  }, [reports.length]);

  const [reportHtml, setReportHtml] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);

  const r = reports.length > 0 ? reports[selectedIdx] : undefined;

  useEffect(() => {
    if (!r) return;
    setHtmlLoading(true);
    setReportHtml(null);
    axiosConfig.get(htmlEndpoint, {
      params: { installationId, language: intl.locale, source: 'web', ...htmlParamsFn(r) },
      responseType: 'text'
    })
      .then(res => setReportHtml(res.data))
      .catch(() => setReportHtml(null))
      .finally(() => setHtmlLoading(false));
  }, [r?.periodStart, r?.periodEnd, intl.locale, installationId]);

  if (reports.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Alert severity="warning">
          <FormattedMessage id="noReportData" defaultMessage="No report data found." />
        </Alert>
      </Box>
    );
  }

  const handleSendEmail = async (emailAddress: string) => {
    await axiosConfig.post(sendEndpoint, null, {
      params: { ...sendParamsFn(r!), emailAddress }
    });
  };

  return (
    <>
      {/* Period selector + Email bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {reports.length > 1 && (
          <Select
            value={selectedIdx}
            onChange={(e) => handleIdxChange(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 200 }}
          >
            {reports.map((rep, i) => (
              <MenuItem key={i} value={i}>{labelFn(rep)}</MenuItem>
            ))}
          </Select>
        )}
        <Box sx={{ ml: 'auto' }}>
          {showEmail && <EmailBar onSend={handleSendEmail} />}
        </Box>
      </Box>

      {/* Report content — rendered from backend HTML */}
      {htmlLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={50} style={{ color: '#ffc04d' }} />
        </Box>
      ) : reportHtml ? (
        <ReportHtmlFrame html={reportHtml} />
      ) : null}
    </>
  );
}

// ── Shared Components ──────────────────────────────────────────

function InsightBox({ text, bullets }: { text: string; bullets: string[] }) {
  return (
    <Box
      sx={{
        bgcolor: '#fef9e7',
        borderLeft: '4px solid #f39c12',
        p: 2.5,
        borderRadius: '0 8px 8px 0'
      }}
    >
      {bullets.length > 1 ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.2, lineHeight: 1.7, fontSize: '14px', color: '#333' } }}>
          {bullets.map((bullet, i) => (
            <li key={i}><FormattedBullet text={bullet} /></li>
          ))}
        </Box>
      ) : (
        <Typography sx={{ lineHeight: 1.7, fontSize: '14px', color: '#333' }}>
          <FormattedBullet text={text} />
        </Typography>
      )}
    </Box>
  );
}

export function SavingsCards({ intl, energySaved, savingsCHF, selfSufficiency, batteryEfficiency, hint }: {
  intl: any;
  energySaved: number;
  savingsCHF: number;
  selfSufficiency: number;
  batteryEfficiency: number;
  hint?: string;
}) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'solarEnergyUsed' })}
          value={`${energySaved} kWh`}
          subtitle={intl.formatMessage({ id: 'solarStayedHome' })}
          color="#27ae60"
          hint={hint}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'estMoneySaved' })}
          value={`~${savingsCHF} CHF`}
          subtitle={intl.formatMessage({ id: 'atCHFRate' })}
          color="#2980b9"
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'solarCoverage' })}
          value={`${selfSufficiency.toFixed(0)}%`}
          subtitle={intl.formatMessage({ id: 'fromSolarSub' })}
          color="#8e44ad"
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <SavingsCard
          label={intl.formatMessage({ id: 'batteryEfficiency' })}
          value={`${batteryEfficiency.toFixed(0)}%`}
          subtitle={intl.formatMessage({ id: 'batteryEffSub' })}
          color="#e67e22"
        />
      </Grid>
    </Grid>
  );
}

function SavingsCard({ label, value, subtitle, color, hint }: { label: string; value: string; subtitle: string; color: string; hint?: string }) {
  return (
    <Box
      sx={{
        bgcolor: '#f8f9fa',
        borderRadius: 2,
        p: 2,
        textAlign: 'center'
      }}
    >
      <Typography variant="h4" fontWeight="bold" sx={{ color, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ color: '#444', mt: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: '#888' }}>
        {subtitle}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ display: 'block', color, fontWeight: 'bold', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export default WeeklyReport;
